import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink, faGlobe, faGear } from '@fortawesome/pro-regular-svg-icons';
import { CircularProgress } from '@mui/material';
import { Link } from 'react-router';
import { AsyncRenderer } from '@client/new/components/AsyncRenderer/AsyncRenderer';
import { TitledPage } from '@client/new/components/Misc';
import { FeatureSettings } from '@client/components/FeatureSettings';
import { ConnectedAccountsSection } from '../creator/settings/account/ConnectedAccountsSection';
import { Accordion } from '@design-system/components/accordion/Accordion';
import { AccordionHeader } from './components/AccordionHeader';
import { sectionConfigs } from './components/sectionConfigs';
import { ContactInfoSection } from './contact-info/ContactInfoSection';
import { SignInEmailSection } from './sign-in-email/SignInEmailSection';
import { useSettingsViewModel } from './useSettings.viewModel';
import { PATHS } from '@client/routes';

type SettingsPageProps = {
  params: {
    influencerId: string;
  };
};

export const SettingsPage = (props: SettingsPageProps) => {
  const influencerId = parseInt(props.params.influencerId);
  const viewModel = useSettingsViewModel({ influencerId });

  return (
    <AsyncRenderer
      data={viewModel}
      loadRender={
        <TitledPage title="Settings">
          <div className="flex justify-center items-center">
            <CircularProgress />
          </div>
        </TitledPage>
      }
      renderer={(data) => (
        <TitledPage title="Settings">
          <div className="flex flex-col gap-4 max-w-screen-md">
            {/* Sign-in email */}
            <SignInEmailSection
              signInEmail={data.influencer.signInEmail}
              onRefetch={data.refetch}
            />

            {/* Connected accounts */}
            <Accordion
              defaultOpen={true}
              renderHeader={() => (
                <AccordionHeader
                  icon={<FontAwesomeIcon icon={faLink} />}
                  {...sectionConfigs.connectedAccounts}
                />
              )}
            >
              <ConnectedAccountsSection
                socialAccounts={data.influencer.socialAccounts}
                onRefresh={() => data.refetch()}
              />
            </Accordion>

            {/* Creator contact info */}
            <ContactInfoSection
              influencerId={data.influencer.id}
              email={data.influencer.email}
              mobilePhoneNumber={data.influencer.mobilePhoneNumber}
            />

            {/* Custom domain — static card, no accordion */}
            <div className="bg-white rounded-xl p-4">
              <div className="flex items-center justify-between">
                <AccordionHeader
                  icon={<FontAwesomeIcon icon={faGlobe} />}
                  {...sectionConfigs.customDomain}
                />
                <Link
                  to={PATHS.CUSTOM_DOMAIN_SETUP(data.influencer.id)}
                  className="bg-[#ede5fc] text-[#350584] text-sm font-medium px-4 py-2 rounded-[10px] no-underline whitespace-nowrap"
                >
                  Set up
                </Link>
              </div>
            </div>

            {/* Feature settings */}
            {data.showFeatureSettings ? (
              <Accordion
                defaultOpen={true}
                renderHeader={() => (
                  <AccordionHeader
                    icon={<FontAwesomeIcon icon={faGear} />}
                    {...sectionConfigs.featureSettings}
                  />
                )}
              >
                <FeatureSettings influencer={data.influencer} />
              </Accordion>
            ) : null}
          </div>
        </TitledPage>
      )}
    />
  );
};
